import { type MouseEvent, useCallback } from "react";

import * as Styled from "common/components/form/MultiSelect/MultiSelect.styled";
import type { ClickableOption, MultiSelectOption } from "common/components/form/MultiSelect/types";
import { useMultiSelect } from "common/components/form/MultiSelect/useMultiSelect";
import { CheckIcon, CrossIcon } from "common/components/icons";
import { useTranslate } from "common/utilities/useTranslate";

type MultiSelectProps<T> = {
  accessibilityId?: string;
  values?: MultiSelectOption<T>[];
  placeholder?: string;
  options: ClickableOption<T>[];
  removeOption: (option: MultiSelectOption<T>) => void;
};

export const MultiSelect = <T,>({
  accessibilityId,
  values,
  placeholder,
  options,
  removeOption,
}: MultiSelectProps<T>) => {
  const translate = useTranslate();

  const {
    containerRef,
    inputRef,
    resultsContainerRef,
    input,
    filteredOptionsByInput,
    areOptionsVisible,
    handleInputChange,
    openSelectOptionsList,
    closeSelectOptionsListOnKeyDown,
    selectOption,
    handleInputKeyPress,
  } = useMultiSelect({ values, options, removeOption });

  const renderSelectOptions = useCallback(() => {
    if (filteredOptionsByInput.length === 0)
      return <Styled.NoResultsText>{translate("app.search.results.no_results")}</Styled.NoResultsText>;

    return filteredOptionsByInput.map((option) => (
      <Styled.SelectOption
        key={option.label}
        onClick={(event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          selectOption(option);
        }}
      >
        {option.label}
        {values?.find((value) => value.value === option.value) && <CheckIcon width={14} height={14} />}
      </Styled.SelectOption>
    ));
  }, [filteredOptionsByInput, selectOption, translate, values]);

  const renderSelectedValues = () => {
    if (!values || values.length === 0) return null;

    return values.map((value) => (
      <Styled.SelectedOption key={value.label} onClick={() => removeOption(value)}>
        {value.label}
        <Styled.CrossIconContainer>
          <CrossIcon width={10} height={10} />
        </Styled.CrossIconContainer>
      </Styled.SelectedOption>
    ));
  };

  return (
    <Styled.Container id={accessibilityId} ref={containerRef} onClick={openSelectOptionsList}>
      <Styled.InputContainer>
        <Styled.SelectedOptionsContainer>
          {renderSelectedValues()}
          <Styled.Input
            ref={inputRef}
            placeholder={values?.length === 0 ? placeholder : ""}
            value={input}
            onChange={handleInputChange}
            onClick={openSelectOptionsList}
            onInput={openSelectOptionsList}
            onKeyDown={handleInputKeyPress}
          />
        </Styled.SelectedOptionsContainer>
        <div>
          <Styled.DropdownIcon height={10} width={10} />
        </div>
      </Styled.InputContainer>
      {areOptionsVisible && (
        <Styled.SelectOptionsList ref={resultsContainerRef} onKeyDown={closeSelectOptionsListOnKeyDown}>
          {renderSelectOptions()}
        </Styled.SelectOptionsList>
      )}
    </Styled.Container>
  );
};
